/**
 * "Could we reach this data?" — the one question a panel has to ask before it
 * is allowed to say an athlete has no history.
 *
 * General infrastructure, deliberately not journey-specific: any screen that
 * distinguishes "you have none of this" from "we could not fetch this" needs
 * exactly these predicates, and a `components/dashboard/*` file importing them
 * out of `views/journey/` would be a view-to-view dependency.
 *
 * **`isError` alone is not the whole answer, and finding that out cost a real
 * outage.** With the API container stopped, four of the Journey's queries
 * reached `status: 'error'` and four sat at `status: 'pending'`,
 * `fetchStatus: 'paused'` — TanStack parks a retry when `onlineManager` thinks
 * the browser is offline. A paused query is neither loading (`isLoading` is
 * `isPending && isFetching`, and a paused query is not fetching) nor errored,
 * so a panel branching on `isError` alone fell straight back through to its
 * empty state and told the athlete they had never trained. That is the exact
 * defect the error states exist to fix, wearing a different flag.
 *
 * So the condition is "we cannot show this", not "the request threw". A reader
 * cannot act on the difference between a 502 and a dropped connection, and the
 * note says the same thing of both: it could not be loaded, and here is the
 * request again (`retryUnreachable`).
 *
 * Deliberately structural, not `UseQueryResult`: the sections' tests mock hooks
 * with the two flags they actually exercise, and a full query result is 25
 * fields of ceremony for a two-field question.
 */
export interface QueryReachability {
  isError: boolean
  isPaused: boolean
  isLoading: boolean
  data: unknown
  /** Ask again. A real query result carries it; the house error calls it. */
  refetch: (options?: { cancelRefetch?: boolean }) => unknown
}

/** This query cannot produce data right now: it failed, or its retry is parked. */
export function unreachable(q: Partial<QueryReachability>): boolean {
  return !!q.isError || !!q.isPaused
}

/**
 * Ask again for every source that could not be reached, and leave the ones that
 * answered alone. This is the house error's "Try again".
 *
 * It exists because nothing else asks again. `staleTime` is `Infinity` and a
 * window focus does not refetch (`main.tsx`), so after a 502 a failed query sits
 * failed until the page is reloaded or remounted; only a dropped connection
 * coming back retries by itself. The note used to promise the panel "fills back
 * in as soon as the data is reachable", which was never true after a 502
 * (D5 design review). A retried query goes back to loading, so the note gives
 * way to the panel's skeleton and then to its content or to the note again.
 *
 * A collapsed card's sources name the same query many times over (body
 * composition feeds five Journey panels), and a plain `refetch()` cancels a
 * fetch already running to start another, so one tap sent the same request
 * several times. `cancelRefetch: false` joins the fetch already out instead, so
 * each failed query is asked once (#213).
 */
export function retryUnreachable(sources: Partial<QueryReachability>[]): void {
  for (const q of sources) {
    if (unreachable(q)) void q.refetch?.({ cancelRefetch: false })
  }
}

/** At least one of these could not be reached. */
export function anyUnreachable(queries: Partial<QueryReachability>[]): boolean {
  return queries.some(unreachable)
}

/** At least one of these has not come back yet. */
export function anyLoading(queries: Partial<QueryReachability>[]): boolean {
  return queries.some((q) => !!q.isLoading)
}

/**
 * What one panel is actually going to render. The four states are exactly the
 * four branches every panel in the portrait already has, in the order it takes
 * them:
 *
 *   loading  a source has not come back yet -> skeleton
 *   ready    the model came out non-null    -> content
 *   error    no model, and a source failed  -> "could not be loaded"
 *   empty    no model, and nothing failed   -> "nothing here yet"
 *
 * `hasContent` is the panel's own `model !== null`, so the state is grounded in
 * what the panel can *draw*, not in what its queries happen to hold. That
 * distinction is the whole point — see `failsAsOne`.
 */
export type PanelState = 'loading' | 'ready' | 'error' | 'empty'

export function panelState(
  sources: Partial<QueryReachability>[],
  hasContent: boolean,
): PanelState {
  if (anyLoading(sources)) return 'loading'
  if (hasContent) return 'ready'
  return anyUnreachable(sources) ? 'error' : 'empty'
}

/**
 * Everything a panel's state hook hands on: the sources and "can it draw" that
 * `AsyncPanel` renders from, and the state a section reads to decide whether to
 * collapse. Built once here so the two readers cannot be handed different
 * answers (Spec 2 D5, #171).
 *
 * `emptyDrawsNothing` is for a panel whose empty state is no state at all: the
 * dashboard's scan strip with no scans, its digest with no session, its analysis
 * with none generated. `failsAsOne` needs to know, because a panel that is not on
 * screen cannot be what keeps a section open.
 */
export function panelOf(
  sources: Partial<QueryReachability>[],
  hasContent: boolean,
  { emptyDrawsNothing = false }: { emptyDrawsNothing?: boolean } = {},
) {
  return { sources, hasContent, state: panelState(sources, hasContent), emptyDrawsNothing }
}

/**
 * The collapse, and the only one (Spec 2 D5, #215): **every panel in this group
 * that would draw anything is going to draw the house error.** One card then says
 * once what would otherwise be said four or ten times while looking like four or
 * ten separate faults. It is the A+ ruling on #169, applied to a section and then
 * to a page: one failed panel shows its own card, a section whose panels all
 * failed shows one, a page whose sections all failed shows one.
 *
 * **An empty panel is judged by what it draws.** One that says so ("No groups
 * yet", "No equipment yet", a Journey chapter's own copy) is on screen and is
 * not an apology, so it keeps its section open: that is true information the
 * collapse would otherwise hide, and with one failure beside it there is nothing
 * being repeated. One that draws nothing is not on screen, so it neither keeps a
 * section open nor collapses one alone. The dashboard's panels are all of the
 * second kind, which is why it used to carry a rule of its own: with a warm
 * cache holding "no scans" or "no analysis", an outage there could never
 * collapse. The two rules disagreed exactly on this case, and this one gives
 * each surface the answer it had.
 *
 * It counts *panels that have nothing to draw*, not *queries that are
 * unreachable*, and that is an older correction. The version before required
 * every source to be unreachable and to hold nothing in the cache, so a single
 * warm query anywhere in the section vetoed the collapse. `staleTime` is
 * `Infinity` and the Dashboard, the app's warm path, already fetches body
 * composition, so the realistic outage sequence (browse the Dashboard, API dies,
 * open the Journey) left `bodyQ` holding scans and no section ever collapsed.
 *
 * The other half of that older rule holds by construction: a panel whose model
 * is non-null is `'ready'`, so a section the cache can still draw in full is
 * never replaced by an apology, however many of its queries report failure.
 */
export function failsAsOne(
  panels: { state: PanelState; emptyDrawsNothing?: boolean }[],
): boolean {
  const onScreen = panels.filter((p) => !(p.state === 'empty' && p.emptyDrawsNothing))
  return onScreen.length > 0 && onScreen.every((p) => p.state === 'error')
}

/**
 * Does this query hold anything at all?
 *
 * Arrays are empty or they are not. Objects are the awkward case, and getting
 * them wrong was a real bug: `useRecords` resolves to `{bests, timeline}`, and
 * treating every non-array object as data meant an entirely empty record book
 * read as a full one. An object whose values are *all* arrays is a container
 * for them, so it holds something only when one of them does; anything else —
 * a profile, a summary — is itself the datum.
 */
function holdsData(q: Partial<QueryReachability>): boolean {
  const data = q.data
  if (data === undefined || data === null) return false
  if (Array.isArray(data)) return data.length > 0
  if (typeof data === 'object') {
    const values = Object.values(data)
    if (values.length > 0 && values.every(Array.isArray)) {
      return values.some((v: unknown[]) => v.length > 0)
    }
  }
  return true
}

/**
 * The empty-section collapse: **these panels are all empty, and they are empty
 * for one shared reason.**
 *
 * The owner's ruling, applied identically in all three sections: per-panel
 * empty states when a section is only partially empty, one card when the whole
 * of it is empty for one reason. A brand-new athlete used to meet four
 * `EmptyPanel` cards in Trajectory, one in Body and two in Records — seven ways
 * of saying "nothing yet", which is the pattern Body's own docblock forbade
 * while Trajectory and Records ignored it.
 *
 * Two guards, and both are load-bearing:
 *
 * 1. **Every panel is `'empty'`.** So nothing is loading, nothing failed, and
 *    no panel that could draw is being hidden.
 * 2. **No source holds anything.** Otherwise "there is nothing here yet" is a
 *    claim about the athlete that the data contradicts. A body scan whose
 *    columns are all null blanks every panel downstream of it, but the athlete
 *    *had* a scan — that is an unusable measurement, not an absent one, and it
 *    keeps its per-panel copy.
 *
 * `panels` is a group that shares one reason, not necessarily the whole
 * section. Body has two: the trio downstream of a body scan, and the age curve,
 * which runs on training data and a birthday and is empty for reasons of its
 * own.
 */
export function nothingRecorded(
  sources: Partial<QueryReachability>[],
  panels: { state: PanelState }[],
): boolean {
  return (
    panels.length > 0 &&
    panels.every((p) => p.state === 'empty') &&
    sources.length > 0 &&
    sources.every((q) => !holdsData(q))
  )
}
